import { push } from 'connected-react-router'
import { all, call, put, take, takeEvery } from 'redux-saga/effects'
import { t } from 'ttag'
import { getWhoAmI, getWhoAmIFail, getWhoAmISuccess } from '../../../common/auth/authActions'
import { INotification } from '../../../common/notifications/NotificationReducers'
import { ROUTES } from '../../../common/Routes/routePaths'
import api from '../../../services/api.services'
import {
  registerAppleHealth,
  registerAppleHealthFail,
  registerAppleHealthSuccess,
  registerHealthConnect,
  registerHealthConnectFail,
  registerHealthConnectSuccess,
  registerUser,
  registerUserFail,
  registerUserSuccess,
  registerWithoutIntegration,
  registerWithoutIntegrationFail,
  registerWithoutIntegrationSuccess,
  setOnboardingStatusToActivityData,
  setOnboardingStatusToIntegration,
  updateActivityData,
  updateActivityDataFail,
  updateActivityDataSuccess,
} from './RegisterUserActions'
import { addNotification, clearNotification } from '../../../common/notifications/NotificationActions'
import { initialAppleHealthIntegration, setIntegrationSuccess } from '../../../common/integration/integrationActions'
import { Preferences } from '@capacitor/preferences'
import { getErrorMsg } from '../../../common/helpers/translations'
import { getCompanyDivisions } from '../../Settings/store/SettingsActions'
import { scrollToTop } from '../../../common/helpers/scroll'
import { dataSync } from '../../../services/DataSync'

function* sagaRegisterUser({ payload }: any) {
  try {
    const {
      password,
      firstName,
      lastName,
      divisionId,
      country,
      timeZone,
      divisions,
      ssn,
      phone,
      units,
      //newsletterSubscriber
    } = payload
    yield put({ type: String(clearNotification) })
    yield call(api.activity.updateProfileData, {
      firstName,
      lastName,
      divisionId,
      country,
      timeZone,
      divisions: divisions || [],
      //newsletterSubscriber,
      strict: true,
      directToPreferences: true,
      ssn,
      phone,
      units,
    })
    // Deprecated:
    //yield call(api.auth.registerUser, { password })

    // Update userDto...
    yield put({ type: String(getWhoAmI) })
    yield take([String(getWhoAmISuccess), String(getWhoAmIFail)])

    yield put({ type: String(registerUserSuccess) })
  } catch (e: any) {
    console.error('RegisterUserFailed: ', e)
    yield put({ type: String(registerUserFail) })
    let content: any = getErrorMsg(
      e.message && e.message !== 'No message has been provided by the server' ? e.message : 'edit data',
    )
    if (e.message && e.message === 'maxTeamSizeReached') {
      content = t`Sorry, the selected team has reached its maximum size. Please select another team!`
      // Load teams list again.
      yield put({ type: String(getCompanyDivisions) })
    } else if (e.message && e.message === 'teamIsLocked') {
      content = t`Sorry, the selected team has been locked. Please select another team!`
      // Load teams list again.
      yield put({ type: String(getCompanyDivisions) })
    }

    // Update userDto...
    yield put({ type: String(getWhoAmI) })
    yield take([String(getWhoAmISuccess), String(getWhoAmIFail)])

    const errorNotification: INotification = {
      type: 'warning',
      content,
    }
    yield put({ type: String(addNotification), payload: errorNotification })
  }
}

function* sagaUpdateActivityData({ payload }: any) {
  try {
    const { dateOfBirth, gender, height, weight, units } = payload
    yield put({ type: String(clearNotification) })

    const { value: isActivityIndexFlow } = yield Preferences.get({ key: 'isActivityIndexFlow' })

    yield call(api.activity.updateActivityData, {
      dateOfBirth,
      gender,
      units,
      height,
      weight,
      // If not registration, don't!
      directToPreferences: !isActivityIndexFlow,
    })

    // Update userDto...
    yield put({ type: String(getWhoAmI) })
    yield take([String(getWhoAmISuccess), String(getWhoAmIFail)])

    yield put({ type: String(updateActivityDataSuccess) })
  } catch (e: any) {
    yield put({ type: String(updateActivityDataFail) })
    console.error('UpdateActivityDataFail: ', e)

    const errorNotification: INotification = {
      type: 'warning',
      content: getErrorMsg('activity index'),
    }
    yield put({ type: String(addNotification), payload: errorNotification })
    scrollToTop()
  }
}

function* sagaRegisterAppleHealthUser({ payload }: any) {
  try {
    yield put({ type: String(clearNotification) })
    yield call(api.activity.setAppleHealthIntegration)

    yield put({ type: String(getWhoAmI) })
    yield take([String(getWhoAmISuccess), String(getWhoAmIFail)])
    yield put({ type: String(setIntegrationSuccess) })
    yield put({ type: String(initialAppleHealthIntegration) })

    yield put({ type: String(registerAppleHealthSuccess) })
  } catch (e: any) {
    yield put({ type: String(registerAppleHealthFail) })
    console.error('RegisterAppleHealthFail: ', e)

    const errorNotification: INotification = {
      type: 'warning',
      content: getErrorMsg('apple health integration'),
    }
    yield put({ type: String(addNotification), payload: errorNotification })
    scrollToTop()
  }
}

function* sagaRegisterHealthConnect() {
  try {
    yield put({ type: String(clearNotification) })
    yield call(api.activity.setHealthConnectIntegration)

    // Update userDto...
    yield put({ type: String(getWhoAmI) })
    yield take([String(getWhoAmISuccess), String(getWhoAmIFail)])
    yield put(push(ROUTES.CLIENT_ACTIVITY.to))

    yield put({ type: String(registerHealthConnectSuccess) })
  } catch (e: any) {
    yield put({ type: String(registerHealthConnectFail) })
    console.error('RegisterHealthConnectFail: ', e)

    const errorNotification: INotification = {
      type: 'warning',
      content: getErrorMsg('health connect integration'),
    }
    yield put({ type: String(addNotification), payload: errorNotification })
    scrollToTop()
  }
}

function* sagaRegisterHealthConnectSuccess() {
  try {
    const toggleSuccessNotification: INotification = { content: t`Integration success!`, type: 'success' }
    yield put({ type: String(addNotification), payload: toggleSuccessNotification })

    yield dataSync(true, true)
  } catch (e: any) {
    console.log('Error: ', e)
  }
}

function* sagaRegisterWithoutIntegration() {
  try {
    yield put({ type: String(clearNotification) })
    yield call(api.activity.setNoIntegration)

    // Update userDto...
    yield put({ type: String(getWhoAmI) })
    yield take([String(getWhoAmISuccess), String(getWhoAmIFail)])
    yield put(push(ROUTES.CLIENT_ACTIVITY.to))

    yield put({ type: String(registerWithoutIntegrationSuccess) })
  } catch (e: any) {
    yield put({ type: String(registerWithoutIntegrationFail) })
    console.error('RegisterWithoutIntegrationFail: ', e)

    const errorNotification: INotification = {
      type: 'warning',
      content: getErrorMsg('set no integration'),
    }
    yield put({ type: String(addNotification), payload: errorNotification })
    scrollToTop()
  }
}

function* sagaSetOnboardingStatusToActivityData() {
  try {
    yield put({ type: String(clearNotification) })
    yield call(api.activity.setOnboardingStatusToActivityData)

    yield put({ type: String(getWhoAmI) })
    // Make sure whoAmI is finished, before we proceed.
    yield take([String(getWhoAmISuccess), String(getWhoAmIFail)])
    yield put(push(ROUTES.REGISTER_USER.to))
    yield Preferences.set({ key: 'isActivityIndexFlow', value: '1' })
  } catch (e: any) {
    console.error('SetOnboardingStatusToActivityData failed: ', e)
  }
}

function* sagaSetOnboardingStatusToIntegration() {
  try {
    yield put({ type: String(clearNotification) })
    yield call(api.activity.setOnboardingStatusToIntegration)

    yield put({ type: String(getWhoAmI) })

    // Make sure whoAmI is finished, before we proceed.
    yield take([String(getWhoAmISuccess), String(getWhoAmIFail)])

    yield put(push(ROUTES.REGISTER_USER.to))
  } catch (e: any) {
    console.error('SetOnboardingStatusToIntegration failed: ', e)
  }
}

export function* registerUserSagas() {
  yield all([
    takeEvery(String(registerUser), sagaRegisterUser),
    takeEvery(String(registerAppleHealth), sagaRegisterAppleHealthUser),
    takeEvery(String(registerHealthConnect), sagaRegisterHealthConnect),
    takeEvery(String(registerWithoutIntegration), sagaRegisterWithoutIntegration),
    takeEvery(String(setOnboardingStatusToActivityData), sagaSetOnboardingStatusToActivityData),
    takeEvery(String(setOnboardingStatusToIntegration), sagaSetOnboardingStatusToIntegration),
    takeEvery(String(updateActivityData), sagaUpdateActivityData),
    takeEvery(String(registerHealthConnectSuccess), sagaRegisterHealthConnectSuccess),
  ])
}
